import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import type { IntakeAccessoryStatus } from '@/services/equipment-intake.service';
import { Input } from '@/components/ui/input';
import {
  ACCESSORY_STATUSES,
  ACCESSORY_STATUS_LABEL,
  ACCESSORY_STATUS_STYLE,
  ACCESSORY_STATUS_INACTIVE,
} from '../intake-form.constants';

export interface AccessoryRow {
  accessoryId: string | null;
  accessoryName: string;
  status: IntakeAccessoryStatus;
  notes: string;
}

interface AccessoryChecklistProps {
  rows: AccessoryRow[];
  onChange: (rows: AccessoryRow[]) => void;
  disabled: boolean;
}

/** Structured accessory reconciliation — reused verbatim from the original
 * EquipmentIntakePage modal form; behavior unchanged, only extracted so the
 * Equipment Intake Workspace (Feature 5) can reuse it without duplication. */
export function AccessoryChecklist({ rows, onChange, disabled }: AccessoryChecklistProps) {
  const received = rows.filter((r) => r.status === 'RECEIVED').length;
  const missing = rows.filter((r) => r.status === 'MISSING').length;
  const damaged = rows.filter((r) => r.status === 'DAMAGED').length;

  const setStatus = (idx: number, status: IntakeAccessoryStatus) => {
    const next = [...rows];
    next[idx] = { ...next[idx]!, status, notes: status !== 'DAMAGED' ? '' : next[idx]!.notes };
    onChange(next);
  };

  const setNotes = (idx: number, notes: string) => {
    const next = [...rows];
    next[idx] = { ...next[idx]!, notes };
    onChange(next);
  };

  const setName = (idx: number, name: string) => {
    const next = [...rows];
    next[idx] = { ...next[idx]!, accessoryName: name };
    onChange(next);
  };

  const remove = (idx: number) => onChange(rows.filter((_, i) => i !== idx));
  const addCustom = () =>
    onChange([...rows, { accessoryId: null, accessoryName: '', status: 'RECEIVED', notes: '' }]);

  return (
    <div className="space-y-3">
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="rounded-full bg-green-500/10 px-2.5 py-0.5 text-green-700">
            {received} Received
          </span>
          {missing > 0 && (
            <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-amber-700">
              {missing} Missing
            </span>
          )}
          {damaged > 0 && (
            <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-red-700">
              {damaged} Damaged
            </span>
          )}
        </div>
      )}

      {rows.map((row, idx) => (
        <div key={idx} className="rounded-lg border border-border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            {row.accessoryId === null ? (
              <Input
                value={row.accessoryName}
                onChange={(e) => setName(idx, e.target.value)}
                placeholder="Accessory name"
                disabled={disabled}
                className="flex-1 text-sm h-8"
              />
            ) : (
              <span className="flex-1 text-sm font-medium text-foreground">
                {row.accessoryName}
              </span>
            )}
            <button
              type="button"
              onClick={() => remove(idx)}
              disabled={disabled}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40"
              aria-label="Remove"
            >
              <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
            </button>
          </div>

          <div className="flex gap-1.5">
            {ACCESSORY_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                disabled={disabled}
                onClick={() => setStatus(idx, s)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                  row.status === s ? ACCESSORY_STATUS_STYLE[s] : ACCESSORY_STATUS_INACTIVE
                }`}
              >
                {ACCESSORY_STATUS_LABEL[s]}
              </button>
            ))}
          </div>

          {row.status === 'DAMAGED' && (
            <Input
              value={row.notes}
              onChange={(e) => setNotes(idx, e.target.value)}
              placeholder="Describe the damage…"
              disabled={disabled}
              className="text-sm h-8"
            />
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addCustom}
        disabled={disabled}
        className="flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-40"
      >
        <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
        Add accessory not in list
      </button>
    </div>
  );
}
